import axios from 'axios';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:7070';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // Token will be added by the calling component that has access to Clerk
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized errors
      console.error('Unauthorized access');
    }
    return Promise.reject(error);
  }
);

/** Allowed answer.source values (must match backend ANSWER_SOURCES) */
export type AnswerSource = 'gemini' | 'chatgpt' | 'perplexity' | 'claude' | 'copilot' | 'openrouter';

export const ANSWER_SOURCE_LABELS: Record<AnswerSource, string> = {
  gemini: 'Gemini',
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  claude: 'Claude',
  copilot: 'Copilot',
  openrouter: 'OpenRouter',
};

export function getAnswerSourceLabel(source: string): string {
  return ANSWER_SOURCE_LABELS[source as AnswerSource] ?? source;
}

export const promptAPI = {
  listSharedWithMe: async (page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/prompts/shared-with-me?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  share: async (id: number, username: string, token: string) => {
    return api.post(`/api/prompts/${id}/share`, { username }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  unshare: async (id: number, recipientId: number, token: string) => {
    return api.delete(`/api/prompts/${id}/share/${recipientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
};

// Answer API methods
export const answerAPI = {
  create: async (data: {
    title?: string;
    content?: string;
    media_url?: string;
    source?: AnswerSource;
    is_public?: boolean;
    tags?: string[];
  }, token: string) => {
    return api.post('/api/answers', data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  get: async (id: number, token: string) => {
    return api.get(`/api/answers/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  update: async (id: number, data: {
    title?: string;
    content?: string;
    media_url?: string;
    is_public?: boolean;
    tags?: string[];
  }, token: string) => {
    return api.put(`/api/answers/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  delete: async (id: number, token: string) => {
    return api.delete(`/api/answers/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  list: async (params: {
    scope?: 'private' | 'public' | 'all';
    search?: string;
    q?: string;
    tags?: string[];
    source?: AnswerSource;
    page?: number;
    per_page?: number;
  }, token: string) => {
    const queryParams = new URLSearchParams();
    if (params.scope) queryParams.append('scope', params.scope);
    if (params.search) queryParams.append('search', params.search);
    if (params.q) queryParams.append('q', params.q);
    if (params.tags) queryParams.append('tags', params.tags.join(','));
    if (params.source) queryParams.append('source', params.source);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.per_page) queryParams.append('per_page', params.per_page.toString());

    return api.get(`/api/answers?${queryParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  listTop: async (limit: number = 20, token: string) => {
    return api.get(`/api/answers/top?limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  listFollowing: async (page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/answers/following?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  listLiked: async (page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/answers/liked?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  listMy: async (page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/answers/my?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  getPublic: async (shareId: string) => {
    return api.get(`/api/answers/public/${shareId}`);
  },

  listSharedWithMe: async (page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/answers/shared-with-me?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  share: async (id: number, username: string, token: string) => {
    return api.post(`/api/answers/${id}/share`, { username }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  unshare: async (id: number, recipientId: number, token: string) => {
    return api.delete(`/api/answers/${id}/share/${recipientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  export: async (token: string) => {
    return api.get('/api/answers/export', {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });
  },

  like: async (id: number, token: string) => {
    return api.post(`/api/answers/${id}/like`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  unlike: async (id: number, token: string) => {
    return api.delete(`/api/answers/${id}/like`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  getReplies: async (id: number, page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/answers/${id}/replies?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  createReply: async (id: number, content: string, parent_reply_id?: number, token?: string) => {
    return api.post(`/api/answers/${id}/replies`, {
      content,
      parent_reply_id
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  deleteReply: async (replyId: number, token: string) => {
    return api.delete(`/api/answer-replies/${replyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  getAnswerFolders: async (answerId: number, token: string) => {
    return api.get(`/api/folders/answer/${answerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  setAnswerFolders: async (answerId: number, folderIds: number[], token: string) => {
    return api.put(`/api/folders/answer/${answerId}`, {
      folder_ids: folderIds
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  addAnswerToFolder: async (folderId: number, answerId: number, token: string) => {
    return api.post(`/api/folders/${folderId}/answers/${answerId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  removeAnswerFromFolder: async (folderId: number, answerId: number, token: string) => {
    return api.delete(`/api/folders/${folderId}/answers/${answerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  listFolderAnswers: async (folderId: number, page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/folders/${folderId}/answers?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  listFolderSkills: async (folderId: number, page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/folders/${folderId}/skills?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  addSkillToFolder: async (folderId: number, skillId: number, token: string) => {
    return api.post(`/api/folders/${folderId}/skills/${skillId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  removeSkillFromFolder: async (folderId: number, skillId: number, token: string) => {
    return api.delete(`/api/folders/${folderId}/skills/${skillId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  getSkillFolders: async (skillId: number, token: string) => {
    return api.get(`/api/folders/skill/${skillId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  setSkillFolders: async (skillId: number, folderIds: number[], token: string) => {
    return api.put(`/api/folders/skill/${skillId}`, {
      folder_ids: folderIds
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }
};

export const skillAPI = {
  listCategories: async (token: string) => {
    return api.get('/api/skills/categories', {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  list: async (params: {
    q?: string;
    tags?: string;
    category?: string;
    view?: 'latest' | 'top' | 'bookmarked';
    page?: number;
    per_page?: number;
  }, token: string) => {
    const q = new URLSearchParams();
    if (params.q) q.append('q', params.q);
    if (params.tags) q.append('tags', params.tags);
    if (params.category) q.append('category', params.category);
    if (params.view) q.append('view', params.view);
    if (params.page) q.append('page', String(params.page));
    if (params.per_page) q.append('per_page', String(params.per_page));
    return api.get(`/api/skills?${q.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  listMy: async (params: {
    q?: string;
    tags?: string;
    page?: number;
    per_page?: number;
  }, token: string) => {
    const q = new URLSearchParams();
    if (params.q) q.append('q', params.q);
    if (params.tags) q.append('tags', params.tags);
    if (params.page) q.append('page', String(params.page));
    if (params.per_page) q.append('per_page', String(params.per_page));
    return api.get(`/api/skills/my?${q.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  get: async (id: number, token: string) => {
    return api.get(`/api/skills/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  create: async (data: {
    name: string;
    description?: string;
    markdown_content: string;
    repo_url?: string;
    repo_stars?: number;
    repo_author?: string;
    category?: string;
    tags?: string[];
    is_public?: boolean;
  }, token: string) => {
    return api.post('/api/skills', data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  update: async (id: number, data: Record<string, unknown>, token: string) => {
    return api.put(`/api/skills/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  delete: async (id: number, token: string) => {
    return api.delete(`/api/skills/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  bookmark: async (id: number, token: string) => {
    return api.post(`/api/skills/${id}/bookmark`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  unbookmark: async (id: number, token: string) => {
    return api.delete(`/api/skills/${id}/bookmark`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  listReplies: async (id: number, page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/skills/${id}/replies?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  createReply: async (id: number, content: string, parent_reply_id: number | undefined, token: string) => {
    return api.post(`/api/skills/${id}/replies`, { content, parent_reply_id }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  deleteReply: async (replyId: number, token: string) => {
    return api.delete(`/api/skills/replies/${replyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }
};

// Feedback API
export const feedbackAPI = {
  create: async (content: string, token: string) => {
    return api.post('/api/feedback', { content }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  list: async (params: {
    scope: 'my' | 'all';
    page?: number;
    per_page?: number;
    sort?: 'recent' | 'top';
    search?: string;
  }, token: string) => {
    const queryParams = new URLSearchParams();
    queryParams.append('scope', params.scope);
    if (params.page != null) queryParams.append('page', params.page.toString());
    if (params.per_page != null) queryParams.append('per_page', params.per_page.toString());
    if (params.scope === 'all') {
      if (params.sort) queryParams.append('sort', params.sort);
      if (params.search) queryParams.append('search', params.search);
    }
    return api.get(`/api/feedback?${queryParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  upvoteToggle: async (feedbackId: number, token: string) => {
    return api.post(`/api/feedback/${feedbackId}/upvote`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
};



export type XMarkContentType = 'tool' | 'article' | 'media' | 'news' | 'tip' | 'other';

export const XMARK_CONTENT_TYPE_LABELS: Record<XMarkContentType, string> = {
  tool: 'Tool',
  article: 'Article',
  media: 'Media',
  news: 'News',
  tip: 'Tip',
  other: 'Other',
};

export const xmarkAPI = {
  list: async (params: {
    q?: string;
    tags?: string[];
    content_type?: XMarkContentType;
    page?: number;
    per_page?: number;
  }, token: string) => {
    const queryParams = new URLSearchParams();
    if (params.q) queryParams.append('q', params.q);
    if (params.tags?.length) queryParams.append('tags', params.tags.join(','));
    if (params.content_type) queryParams.append('content_type', params.content_type);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.per_page) queryParams.append('per_page', params.per_page.toString());
    return api.get(`/api/xmarks?${queryParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  listSharedWithMe: async (page: number = 1, per_page: number = 20, token: string) => {
    return api.get(`/api/xmarks/shared-with-me?page=${page}&per_page=${per_page}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  get: async (id: number, token: string) => {
    return api.get(`/api/xmarks/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  create: async (data: {
    title?: string;
    content: string;
    content_type?: XMarkContentType;
    category?: string;
    source_url?: string;
    media_url?: string;
    author_handle?: string;
    is_public?: boolean;
    tags?: string[];
  }, token: string) => {
    return api.post('/api/xmarks', data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  update: async (id: number, data: {
    title?: string;
    content?: string;
    content_type?: XMarkContentType;
    category?: string;
    is_public?: boolean;
    tags?: string[];
  }, token: string) => {
    return api.put(`/api/xmarks/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  delete: async (id: number, token: string) => {
    return api.delete(`/api/xmarks/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  share: async (id: number, username: string, token: string) => {
    return api.post(`/api/xmarks/${id}/share`, { username }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  unshare: async (id: number, recipientId: number, token: string) => {
    return api.delete(`/api/xmarks/${id}/share/${recipientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
};

export type PromptBadge = 'staff_pick' | 'featured' | 'community_favourite';

export const PROMPT_BADGE_LABELS: Record<PromptBadge, string> = {
  staff_pick: 'Staff Pick',
  featured: 'Featured',
  community_favourite: 'Community Favourite',
};

export interface AdminPromptMetrics {
  saves: number;
  views: number;
  forks: number;
  likes: number;
  composite_score: number;
}

export interface AdminPrompt {
  id: number;
  user_id: number;
  title: string;
  content: string;
  category: string | null;
  is_public: boolean;
  is_featured: boolean;
  featured_until: string | null;
  featured_category: string | null;
  badge: PromptBadge | null;
  share_id: string;
  created_at: string;
  updated_at: string;
  user?: { username: string; full_name: string; avatar_url?: string };
  is_suppressed: boolean;
  suppressed_at: string | null;
  featured_at: string | null;
  metrics?: AdminPromptMetrics;
  trending_rank?: number;
}

export interface AdminListResult {
  items: AdminPrompt[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export const authAPI = {
  me: async (token: string) => {
    return api.get('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};

export const adminAPI = {
  listTrending: async (
    params: { sort_by?: string; order?: 'asc' | 'desc'; page?: number; per_page?: number },
    token: string
  ) => {
    const q = new URLSearchParams();
    if (params.sort_by) q.append('sort_by', params.sort_by);
    if (params.order) q.append('order', params.order);
    if (params.page != null) q.append('page', params.page.toString());
    if (params.per_page != null) q.append('per_page', params.per_page.toString());
    return api.get<AdminListResult>(`/api/admin/trending?${q.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  listFeatured: async (params: { page?: number; per_page?: number }, token: string) => {
    const q = new URLSearchParams();
    if (params.page != null) q.append('page', params.page.toString());
    if (params.per_page != null) q.append('per_page', params.per_page.toString());
    return api.get<AdminListResult>(`/api/admin/featured?${q.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  searchPrompts: async (params: { q: string; page?: number; per_page?: number }, token: string) => {
    const q = new URLSearchParams();
    q.append('q', params.q);
    if (params.page != null) q.append('page', params.page.toString());
    if (params.per_page != null) q.append('per_page', params.per_page.toString());
    return api.get<AdminListResult>(`/api/admin/prompts/search?${q.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  suppress: async (id: number, token: string) => {
    return api.post<AdminPrompt>(`/api/admin/prompts/${id}/suppress`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  unsuppress: async (id: number, token: string) => {
    return api.post<AdminPrompt>(`/api/admin/prompts/${id}/unsuppress`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  feature: async (
    id: number,
    body: { featured_at?: string | null; featured_until?: string | null; featured_category?: string | null },
    token: string
  ) => {
    return api.post<AdminPrompt>(`/api/admin/prompts/${id}/feature`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  unfeature: async (id: number, token: string) => {
    return api.post<AdminPrompt>(`/api/admin/prompts/${id}/unfeature`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  setBadge: async (id: number, badge: PromptBadge | null, token: string) => {
    return api.post<AdminPrompt>(`/api/admin/prompts/${id}/badge`, { badge }, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};

export default api;
